import React, { useContext, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Backdrop,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  Link,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Typography,
  Paper,
  AlertTitle,
  ToggleButton,
  ToggleButtonGroup
} from "@mui/material";
import ConfigContext from "../../common/context/ConfigContext";
import bkstApi from "../../common/api";
import BDatePicker from "../../common/component/BDatePicker";
import { shouldDisableDate, getDateRules, getAvailableTimeslotsbyDate } from "../../common/util";
import moment from "moment";
import currency from "currency.js";
import AddressAutocomplete from "../../common/component/AddressAutocomplete";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";

export default function FulfillmentOptions(props) {
  const { cartId, items, options, fulfillment, defaultDate } = props;
  const siteConfig = useContext(ConfigContext);

  const [address, setAddress] = useState("");

  const [provider, setProvider] = useState("");
  const [quote, setQuote] = useState(options.shipping.delivery?.price || "");

  const [error, setError] = useState("");

  const [open, setOpen] = useState(false);
  const [processing, setProcessing] = useState(false);

  const [timeslots, setTimeslots] = useState([]);

  const switchToPickup = () => {
    props.onChange({ ...fulfillment, shipping: "pickup", location: options.shipping.pickup.locations[0], date: defaultDate || "", time: "" });
    handleClose();
  };

  const resetQuote = () => {
    setProvider("");
    setQuote("");
    setError("");
  };

  const onSubmit = (e) => {
    e.preventDefault();
    resetQuote();

    if (address) {
      if (!provider) {
        // get quote from fulfillment provider
        setProcessing(true);
        bkstApi
          .post(`/site/${siteConfig.id}/cart/${cartId}/delivery`, { address, date: fulfillment.date, time: fulfillment.time })
          .then((res) => {
            setQuote(res.data.quote);
            setProvider(res.data.provider);
            setProcessing(false);
          })
          .catch((err) => {
            setError("Sorry! Delivery option is not available for this date/time & address");
            setProcessing(false);
          });
      } else {
        props.onChange({
          ...fulfillment,
          shipping: "delivery",
          quote,
          provider,
          location: { ...address, timeslots: options.shipping.delivery.timeslots, rules: options.shipping.delivery.rules }
        });
        handleClose();
      }
    }
  };

  const handleClose = () => {
    resetQuote();

    setAddress("");
    setOpen(false);
  };

  useEffect(() => {
    if (fulfillment.location && fulfillment.date) {
      resetQuote();

      // check if date is disabled
      if (shouldDisableDate(siteConfig.id, fulfillment.date, items, fulfillment)) {
        setTimeslots([]);
      } else {
        const slotsbyDate = getAvailableTimeslotsbyDate(siteConfig.id, fulfillment.location, fulfillment.date, dateRules);
        setTimeslots(slotsbyDate);
        //setTime(slotsbyDate[(slotsbyDate.length / 2) | 0]);
      }
    }
  }, [fulfillment.location, fulfillment.date]);

  const dateRules = getDateRules(items, fulfillment.location.timeslots);

  let CTA = <></>;
  let CONTENT = <></>;

  const DATE_TIME = (
    <Box pt={2}>
      <Grid container spacing={3}>
        <Grid item sm={6} xs={6}>
          <FormControl variant="outlined" fullWidth>
            <BDatePicker
              value={fulfillment?.date || ""}
              label={`Date`}
              onChange={(d) => props.onChange({ ...fulfillment, date: d, time: "" })}
              disablePast
              required
              shouldDisableDate={(d) => shouldDisableDate(siteConfig.id, d, items, fulfillment)}
            />
            <FormHelperText>{fulfillment.date && moment(fulfillment.date).format("dddd")}</FormHelperText>
          </FormControl>
        </Grid>
        <Grid item sm={6} xs={6}>
          <FormControl variant="outlined" fullWidth required error={Boolean(fulfillment?.date && timeslots.length === 0)}>
            <InputLabel id="time-label">{`Time`}</InputLabel>
            <Select
              value={fulfillment.time || ""}
              labelId="time-label"
              label={`Time`}
              id="time"
              name="time"
              onChange={(e) => props.onChange({ ...fulfillment, time: e.target.value })}
            >
              {timeslots.map((i) => (
                <MenuItem key={i} value={i}>
                  {i}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>{fulfillment?.date && timeslots.length === 0 ? "No available timeslots for this date" : ""}</FormHelperText>
          </FormControl>
        </Grid>
      </Grid>
    </Box>
  );

  if (options.shipping.delivery?.type === "third-party" && options.shipping.delivery?.provider === "doordash") {
    CTA = (
      <Button
        component="a"
        target="_blank"
        href={options.shipping?.delivery?.link}
        variant="contained"
        color="primary"
        fullWidth
        onClick={handleClose}
      >
        Order on DoorDash
      </Button>
    );
    CONTENT = (
      <Box>
        <Typography>
          We offer Delivery through DoorDash. Please click the link below to visit our DoorDash store and place your order for Delivery.
        </Typography>
      </Box>
    );
  } else if (provider) {
    CTA = (
      <Button type="submit" fullWidth variant="contained" color="primary">
        ADD DELIVERY
      </Button>
    );
    CONTENT = (
      <Box mt={2}>
        <Alert severity="success">
          <AlertTitle>Delivery is available!</AlertTitle>Verify address & delivery fee below, then click the Add Delivery button
        </Alert>
        <Box mt={3}>
          <Grid container>
            <Grid item xs={8}>
              <Typography variant="overline" color="textSecondary">
                ADDRESS
              </Typography>
              <Typography variant="body1">{address.street}</Typography>
              {address.unit && <Typography variant="body1">{address.unit}</Typography>}
              <Typography variant="body1">
                {address.city}, {address.state}, {address.zip}
              </Typography>
              <Link
                size="small"
                sx={{ cursor: "pointer" }}
                onClick={() => {
                  setProvider("");
                  setAddress("");
                }}
              >
                change
              </Link>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="overline" color="textSecondary">
                DELIVERY FEE
              </Typography>
              <Typography variant="h3">{currency(quote).format()}</Typography>
            </Grid>
          </Grid>
        </Box>
      </Box>
    );
  } else {
    CTA = (
      <Button type="submit" fullWidth variant="contained" color="primary">
        CHECK AVAILABILITY
      </Button>
    );
    CONTENT = (
      <Box>
        <Box mb={2}>{DATE_TIME}</Box>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <AddressAutocomplete
              value={address}
              onPlaceSelect={(o) => {
                setProvider("");
                setQuote("");
                setAddress(o);
              }}
            />
          </Grid>
        </Grid>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="overline" color="textSecondary">
        FULFILLMENT
      </Typography>
      <Paper elevation={0}>
        <Box px={2} pt={2} pb={2}>
          <ToggleButtonGroup color="primary" value={fulfillment.shipping} exclusive fullWidth>
            <ToggleButton value="pickup" onClick={switchToPickup}>
              Pickup
            </ToggleButton>
            <ToggleButton
              value="delivery"
              onClick={() => {
                props.onChange({
                  ...fulfillment,
                  shipping: "delivery",
                  location: { timeslots: options.shipping.delivery.timeslots, rules: options.shipping.delivery.rules }
                });

                setOpen(true);
              }}
              disabled={!options.shipping.delivery}
            >
              Delivery {!options.shipping.delivery && ` (not available)`}
            </ToggleButton>
          </ToggleButtonGroup>
          <Box mt={3}>
            <Box px={1}>
              <Box mb={1}>
                <Typography variant="overline" color="textSecondary">
                  WHERE & WHEN?
                </Typography>
              </Box>
              {fulfillment.shipping === "pickup" && (
                <Box>
                  <RadioGroup
                    name="street"
                    value={options.shipping.pickup.locations.findIndex((x) => x.street === fulfillment.location.street)}
                    onChange={(e) =>
                      props.onChange({ ...fulfillment, shipping: "pickup", location: options.shipping.pickup.locations[e.target.value], time: "" })
                    }
                  >
                    {options.shipping.pickup.locations.map((o, idx) => {
                      return (
                        <Box key={idx} py={0.5}>
                          <FormControlLabel value={idx} control={<Radio />} label={`${o.street}, ${o.city}, ${o.state} ${o.zip}`} />
                        </Box>
                      );
                    })}
                  </RadioGroup>
                  {DATE_TIME}
                </Box>
              )}
              {fulfillment.shipping === "delivery" && (
                <Box>
                  <Box sx={{ display: "flex", gap: "15px" }}>
                    <Box>
                      <LocalShippingOutlinedIcon fontSize="large" sx={{ color: "#777" }} />
                    </Box>
                    <Box>
                      <Typography variant="subtitle1">{fulfillment.location.street}</Typography>
                      {fulfillment.location.unit && <Typography variant="body1">{fulfillment.location.unit}</Typography>}
                      <Typography variant="subtitle1">
                        {fulfillment.location.city}, {fulfillment.location.state}, {fulfillment.location.zip}
                      </Typography>
                      <Box my={0.5}>
                        <Typography variant="body1">
                          {moment(fulfillment.date).format("ddd, MMM D, YYYY")} ● {fulfillment.time || ""}
                        </Typography>
                      </Box>
                      <Link size="small" sx={{ cursor: "pointer" }} onClick={() => setOpen(true)}>
                        change
                      </Link>
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Paper>

      <Dialog open={open} scroll="paper" fullWidth={true} maxWidth="sm">
        <form onSubmit={onSubmit}>
          <DialogTitle>
            {provider ? "Add Delivery" : "Delivery | Check Availability"}
            {/* <IconButton
              aria-label="close"
              onClick={handleClose}
              sx={{
                position: "absolute",
                right: 8,
                top: 8
              }}
            >
              <CloseIcon />
            </IconButton> */}
          </DialogTitle>
          <DialogContent>
            <Box>{CONTENT}</Box>
            {error && (
              <Box mt={2}>
                <Alert severity="error">{error}</Alert>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button fullWidth variant="outlined" color="secondary" onClick={switchToPickup}>
              Switch to Pickup
            </Button>
            {CTA}
          </DialogActions>
        </form>
      </Dialog>
      {processing && (
        <Backdrop open={true} style={{ zIndex: "2000", color: "#fff" }}>
          <CircularProgress color="inherit" />
          <Box p={2} sx={{ display: "block" }}>
            <Typography sx={{ fontSize: "1.2rem" }}>Checking Availability...</Typography>
            <Typography>This may take 5-10 seconds</Typography>
          </Box>
        </Backdrop>
      )}
    </Box>
  );
}
